/**
 * @fileoverview Scenario Conversation Detail
 * @description Renders the evaluated chat transcript, fact coverage, and the
 *              evidence quotes that justified each evaluator verdict.
 */

import React from 'react';
import {
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle2, CircleSlash, MessageSquare, Quote } from 'lucide-react';
import { BAND_COLORS } from '../constants';

// Restrict uploaded links to http(s) so javascript: URLs can't execute.
const toSafeHttpUrl = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value.trim()) ? value : undefined;

/**
 * Render a single evaluated fact with its supporting evidence.
 * @param {Object} props - Fact record and coverage flag.
 * @returns {JSX.Element} Fact entry.
 */
function FactEntry({ fact, covered }) {
  const tone = covered ? BAND_COLORS.green : BAND_COLORS.amber;
  const Icon = covered ? CheckCircle2 : CircleSlash;
  const evidence = Array.isArray(fact.evidence) ? fact.evidence : [];

  return (
    <Box className="aier-fact">
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ color: tone, mt: '2px' }}><Icon size={15} /></Box>
        <Box flex={1}>
          <Typography className="aier-fact-description">{fact.description || fact.id}</Typography>
          {fact.status && (
            <Chip
              size="small"
              label={fact.status}
              className="aier-fact-status"
              sx={{ color: tone, backgroundColor: `${tone}16` }}
            />
          )}
          {fact.reason && <Typography className="aier-fact-reason">{fact.reason}</Typography>}
          {evidence.map((item) => (
            <Box key={`${item.agentTurn}-${item.quote}`} className="aier-evidence">
              <Quote size={12} />
              <Typography className="aier-evidence-quote">
                {item.quote}
                {item.agentTurn !== undefined && (
                  <span className="aier-evidence-turn"> — agent turn {item.agentTurn}</span>
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Box>
  );
}

/**
 * Render the conversation and fact coverage behind a scenario score.
 * @param {Object} props - Normalized scenario conversation.
 * @returns {JSX.Element|null} Conversation detail.
 */
export function ScenarioConversation({ conversation }) {
  if (!conversation) {
    return null;
  }

  const { responses = [], coveredFacts = [], missingFacts = [], referenceSources = [] } = conversation;

  return (
    <Stack spacing={2.5} className="aier-conversation">
      <Box>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={conversation.passed ? 'Passed' : 'Failed'}
            sx={{
              color: conversation.passed ? BAND_COLORS.green : BAND_COLORS.red,
              backgroundColor: `${conversation.passed ? BAND_COLORS.green : BAND_COLORS.red}16`,
              fontWeight: 700,
            }}
          />
          <Chip size="small" variant="outlined" label={`${conversation.turns} turns`} />
          <Chip size="small" variant="outlined" label={conversation.evaluationMode} />
          {conversation.model && <Chip size="small" variant="outlined" label={conversation.model} />}
        </Stack>
      </Box>

      {conversation.purpose && (
        <Box>
          <Typography className="aier-metric-label">Purpose</Typography>
          <Typography className="aier-drawer-value">{conversation.purpose}</Typography>
        </Box>
      )}

      {conversation.endGoal && (
        <Box>
          <Typography className="aier-metric-label">End goal</Typography>
          <Typography className="aier-drawer-value">{conversation.endGoal}</Typography>
        </Box>
      )}

      {(coveredFacts.length > 0 || missingFacts.length > 0) && (
        <Box>
          <Typography className="aier-metric-label">
            Fact coverage ({coveredFacts.length} covered · {missingFacts.length} missing)
          </Typography>
          <Stack spacing={1} mt={1}>
            {coveredFacts.map((fact) => (
              <FactEntry key={fact.id} fact={fact} covered />
            ))}
            {missingFacts.map((fact) => (
              <FactEntry key={fact.id} fact={fact} covered={false} />
            ))}
          </Stack>
        </Box>
      )}

      {responses.length > 0 && (
        <Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <MessageSquare size={14} color="#0097A9" />
            <Typography className="aier-metric-label">
              Conversation ({responses.length} {responses.length === 1 ? 'message' : 'messages'})
            </Typography>
          </Stack>
          <Stack spacing={1.25} mt={1}>
            {responses.map((message, index) => (
              <Box
                key={`${message.role}-${index}`}
                className={`aier-message aier-message--${String(message.role || 'agent').toLowerCase()}`}
              >
                <Typography className="aier-message-role">{message.role || 'AGENT'}</Typography>
                <Typography className="aier-message-text">{message.text}</Typography>
                {Array.isArray(message.links) && message.links.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mt={1}>
                    {message.links.map((link) => (
                      <Link
                        key={link.href}
                        href={toSafeHttpUrl(link.href)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aier-message-link"
                      >
                        {link.text || link.href}
                      </Link>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {referenceSources.length > 0 && (
        <Box>
          <Typography className="aier-metric-label">Reference sources</Typography>
          <Stack spacing={0.5} mt={0.5}>
            {referenceSources.map((source) => (
              <Link
                key={source}
                href={toSafeHttpUrl(source)}
                target="_blank"
                rel="noopener noreferrer"
                className="aier-message-link"
              >
                {source}
              </Link>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

export default ScenarioConversation;
